import { create } from 'zustand'
import { toast } from 'sonner'
import { api, extractApiError } from '../lib/api'
import { authResponse, meResponse } from '../lib/types'
import { userHasAnyOperation, userHasOperation } from '../lib/permissions'
import { connectSocket, disconnectSocket } from '../lib/socket'
import { setDisplayTimeZone } from '../lib/format'
import { clearQueryCache } from '../lib/queryClient'
import { clearPagePerf, flushPagePerf } from '../lib/page-perf/control'
import {
  identityKeyOf,
  installIdentitySync,
  publishIdentity,
} from '../lib/identity-sync'

/** Default toast copy keyed by the server-side termination reason. The
 *  detail event may pass a more specific message; we fall back to these
 *  when it doesn't. */
const FORCE_LOGOUT_MESSAGES = {
  user_deleted: 'Your account has been deleted.',
  user_deactivated:
    'Your account has been deactivated.',
  tenant_deleted:
    'Your company has been deleted. Please contact support.',
  tenant_suspended:
    'Your company has been suspended. Please contact support for assistance.',
  // New: emitted by Platform Admin's "Offboard company" action. Same
  // hard-logout shape as suspended, different message so the user
  // sees an accurate reason on the toast.
  tenant_offboarded:
    'Your company has been offboarded. Please contact support.',
  password_changed:
    'Your password was changed. Please sign in again.',
  // §3 IP access controls.
  disallowed_country:
    'This account is not available in your region. Your plan can only be accessed from the country where it was purchased.',
  ip_blocked:
    'Access from your network has been blocked. Contact your administrator if you believe this is a mistake.',
}

const REMEMBER_KEY = 'taskmagix.rememberMe'
const REMEMBERED_EMAIL_KEY = 'taskmagix.rememberedEmail'

/** Normalise the `/auth/me` user payload into the shape the rest of
 *  the app reads off `useAuthStore.user`. Centralises the work so
 *  every entry point (bootstrap, sign-in, refresh) carries the same
 *  Work Role Management fields — without this, the second-stage
 *  `refreshUser` could drop `operations` and silently flip the user
 *  back to the Employee baseline UI.
 *
 *  Accepts the new `workRoleLevel` payload field. The legacy
 *  `workRoleTier` alias (still emitted by older backends + kept on
 *  the response for back-compat) is used as a fallback so a
 *  frontend-ahead-of-backend deploy still resolves correctly.
 *
 *  When the backend hasn't been updated yet, the fallbacks here keep
 *  the legacy behaviour intact:
 *   - `operations` defaults to `[]`
 *   - `workRoleLevel` defaults to the `workRole` slug
 *   - `workRoleId` / `workRoleName` default to null
 *  Every operation check then falls through to the level-based
 *  legacy path because the `operations` array is empty. */
function buildUser(u) {
  const level = u.workRoleLevel ?? u.workRoleTier ?? u.workRole ?? 'employee'
  return {
    id: u.id,
    tenantId: u.tenantId,
    emailId: u.emailId,
    fullName: u.fullName,
    avatarUrl: u.avatarUrl ?? null,
    role: u.role,
    // [External Collaboration] internal | external. Drives hiding of
    // internal-only surfaces (e.g. the Files & Links page) for externals.
    membershipType: u.membershipType ?? 'internal',
    workRole: u.workRole ?? 'employee',
    workRoleId: u.workRoleId ?? null,
    workRoleName: u.workRoleName ?? null,
    workRoleLevel: level,
    workRoleLevelLabel: u.workRoleLevelLabel ?? null,
    departmentId: u.departmentId ?? null,
    departmentName: u.departmentName ?? null,
    operations: Array.isArray(u.operations) ? u.operations : [],
    // Sub Admin delegated operations. Empty for `user` and
    // `company_admin` accounts — Company Admin gates pass via the
    // `hasSubAdminOperation` role short-circuit, so this array is only
    // meaningful when `role === 'sub_admin'`.
    subAdminOperations: Array.isArray(u.subAdminOperations)
      ? u.subAdminOperations
      : [],
    // Google SSO state — both drive the "Complete Profile" gate in
    // AdminLayout. `profileComplete` defaults to `true` (so any user
    // whose API response predates this field — email-signup users,
    // existing accounts created before the SSO migration — never
    // triggers the gate). `googleLinked` defaults to `false` for the
    // same reason. The gate only fires when BOTH are the exception
    // values (linked AND incomplete), which is structurally
    // impossible for an email/password registration.
    profileComplete: u.profileComplete !== false,
    googleLinked: u.googleLinked === true,
    // Password-credential presence. `!== false` so an absent field reads as
    // TRUE — the safe default, since the only thing this relaxes is whether
    // the current-password proof can be skipped.
    hasPassword: u.hasPassword !== false,
  }
}

function readStoredBool(key) {
  try {
    return localStorage.getItem(key) === '1'
  } catch {
    return false
  }
}
function readStoredString(key) {
  try {
    return localStorage.getItem(key) ?? ''
  } catch {
    return ''
  }
}

/** Dedupe wrapper for `bootstrap()` — guarantees that even if React
 *  StrictMode double-invokes the mount effect (or two components fire
 *  bootstrap concurrently for any other reason), only ONE in-flight
 *  `/auth/refresh` POST exists at a time. Without this, two concurrent
 *  refreshes race against the server's atomic compare-and-revoke:
 *  the first call consumes the refresh-token row, and the second sees
 *  it already revoked and 401s — even though the user is happily
 *  signed in. Mirrors the same pattern used by `refreshSession()`
 *  in lib/api.js. */
let bootstrapPromise = null

/** [QA-04] A sign-in on a tab that already holds another identity (the
 *  "add account" flow runs while signed in) must not let the previous
 *  session's cached responses render for the new one — query keys name the
 *  resource, not the viewer. Call BEFORE adopting `nextUser`. */
function dropCacheOnIdentityChange(prevUser, nextUser) {
  const prev = identityKeyOf(prevUser)
  if (prev && prev !== identityKeyOf(nextUser)) clearQueryCache()
}

export const useAuthStore = create((set, get) => ({
  user: null,
  tenant: null,
  /** Impersonation marker (§1). Non-null only when a Super Admin is
   *  viewing the app "as" another user — drives the sticky banner. */
  impersonation: null,
  /** 'unknown' before the initial /me check resolves; drives the protected-route splash. */
  status: 'unknown',
  /** Mirrors `me.user.workspaceCount`. 0 = show the User-panel onboarding wizard. */
  workspaceCount: 0,
  /** Account switcher — accounts this browser has authenticated (the
   *  person's own accounts across companies, plus any other logins added
   *  on this device). Populated from /auth/me and refreshable on demand. */
  accounts: [],
  rememberMe: readStoredBool(REMEMBER_KEY),
  rememberedEmail: readStoredString(REMEMBERED_EMAIL_KEY),

  async bootstrap() {
    // Reuse any bootstrap already in flight (covers React StrictMode's
    // double-invoke of the mount effect) so we only fire one /auth/me +
    // one store update per load. Any refresh the interceptor triggers on
    // a 401 is itself deduped in lib/api.js (`refreshSession`), so the
    // single-use refresh token never rotates twice concurrently.
    if (bootstrapPromise) return bootstrapPromise
    bootstrapPromise = get()
      ._bootstrap()
      .finally(() => {
        bootstrapPromise = null
      })
    return bootstrapPromise
  },

  /** Internal — the actual bootstrap work. Always go through
   *  `bootstrap()` from outside the store so the dedupe applies. */
  async _bootstrap() {
    // Bootstrap from the EXISTING access cookie via /auth/me. We do NOT
    // proactively POST /auth/refresh on every page load any more.
    //
    // Why this changed: refresh tokens are single-use (the server
    // atomically revokes the presented token and issues a rotated one).
    // Rotating on every load meant a rapid browser reload could cancel
    // the /auth/refresh response AFTER the server revoked the old token
    // but BEFORE the browser stored the rotated cookie — leaving a now-
    // revoked token that fails the next refresh and bounces the user to
    // sign-in. That's the "refresh a page 4-5 times → auto logged out"
    // bug.
    //
    // Instead:
    //   • Access cookie still valid (its ~15-min lifetime) → /auth/me
    //     returns 200 with NO token rotation. Rapid reloads are safe.
    //   • Access cookie expired → the axios 401 interceptor performs a
    //     single, deduped /auth/refresh and retries /auth/me. The refresh
    //     token rotates ONLY when genuinely needed, and that one
    //     rotation's response is always awaited so the new cookie sticks.
    //   • No session at all (fresh visitor) → /auth/me 401 → the
    //     interceptor's refresh gets `{ session: false }` (no terminal
    //     error, no force-logout) → we land on `unauthenticated` cleanly.
    try {
      const res = await api.get('/auth/me')
      const parsed = meResponse.parse(res.data)
      set({
        user: buildUser(parsed.user),
        tenant: parsed.tenant,
        impersonation: parsed.impersonation ?? null,
        status: 'authenticated',
        workspaceCount: parsed.user.workspaceCount ?? 0,
        accounts: parsed.availableAccounts ?? [],
      })
      // Render every date in the tenant's timezone (multi-country SaaS),
      // not the viewer's browser zone. Set synchronously here so the
      // first authenticated paint already formats in the right zone.
      setDisplayTimeZone(parsed.tenant?.timezone)
      // [QA-04] Tell other tabs whose session the shared cookies now carry
      // (covers the account-switch / impersonation hard reloads).
      publishIdentity(parsed.user)
      // Eager socket connect so server-pushed eviction events
      // (session.terminated) reach the SPA even on pages that don't
      // mount any notifications widget.
      connectSocket()
    } catch {
      set({ user: null, tenant: null, status: 'unauthenticated', workspaceCount: 0 })
      setDisplayTimeZone(undefined)
    }
  },

  async signIn(emailId, password, rememberMe, turnstileToken, tenantId) {
    const res = await api.post('/auth/sign-in', {
      emailId,
      password,
      rememberMe,
      turnstileToken: turnstileToken || undefined,
      // Account-picker disambiguator — set on the re-submit after the
      // user chooses which company to sign into.
      tenantId: tenantId || undefined,
    })
    // Account picker: this email+password matched several companies and
    // no company was chosen yet. No session was established — hand the
    // choices back so SignInPage can render the picker.
    if (res.data?.multipleAccounts) {
      return {
        multipleAccounts: true,
        accounts: res.data.accounts ?? [],
        // Memberships that exist but can't sign in (suspended account,
        // unavailable organisation) — rendered as disabled picker rows
        // with the reason so a blocked account is never silently skipped.
        blockedAccounts: res.data.blockedAccounts ?? [],
      }
    }
    const parsed = authResponse.parse(res.data)
    try {
      localStorage.setItem(REMEMBER_KEY, rememberMe ? '1' : '0')
      localStorage.setItem(REMEMBERED_EMAIL_KEY, rememberMe ? emailId : '')
    } catch {
      // ignore storage errors — not essential
    }
    dropCacheOnIdentityChange(get().user, parsed.user)
    set({
      user: buildUser(parsed.user),
      tenant: parsed.tenant,
      status: 'authenticated',
      rememberMe,
      rememberedEmail: rememberMe ? emailId : '',
    })
    setDisplayTimeZone(parsed.tenant?.timezone)
    publishIdentity(parsed.user)
    connectSocket()
    // /sign-in doesn't currently include workspaceCount; pull it from /me so
    // the User-panel onboarding wizard knows whether to show on first login.
    await get().refreshUser()
    return { multipleAccounts: false }
  },

  async register(input) {
    const res = await api.post('/auth/register', input)
    const parsed = authResponse.parse(res.data)
    dropCacheOnIdentityChange(get().user, parsed.user)
    set({
      user: buildUser(parsed.user),
      tenant: parsed.tenant,
      status: 'authenticated',
    })
    setDisplayTimeZone(parsed.tenant?.timezone)
    publishIdentity(parsed.user)
    connectSocket()
  },

  /** Adopt a Google SSO sign-in payload. The HTTP call lives in the
   *  GoogleSignInButton component (which has direct access to the GSI
   *  credential); this action just hydrates the store the same way
   *  signIn / register do. Used by both the Sign In and Sign Up
   *  pages — the response shape is identical regardless of whether
   *  Google created a new user or matched an existing one. */
  adoptGoogleSession(payload) {
    if (!payload?.user) return
    const parsed = authResponse.parse(payload)
    dropCacheOnIdentityChange(get().user, parsed.user)
    set({
      user: buildUser(parsed.user),
      tenant: parsed.tenant,
      status: 'authenticated',
    })
    setDisplayTimeZone(parsed.tenant?.timezone)
    publishIdentity(parsed.user)
    connectSocket()
    // Refresh /me so workspaceCount + any server-side defaults land in
    // the store on first dashboard paint — same as the email/password
    // path does below.
    void get().refreshUser()
  },

  async refreshUser() {
    try {
      const res = await api.get('/auth/me')
      const parsed = meResponse.parse(res.data)
      set({
        user: buildUser(parsed.user),
        tenant: parsed.tenant,
        impersonation: parsed.impersonation ?? null,
        workspaceCount: parsed.user.workspaceCount ?? 0,
        accounts: parsed.availableAccounts ?? [],
      })
      // Keep date formatting in sync after a session refresh (e.g. right
      // after an admin saves the company timezone).
      setDisplayTimeZone(parsed.tenant?.timezone)
    } catch (err) {
      extractApiError(err)
    }
  },

  /** Overwrite the impersonation marker locally. Set when an impersonate
   *  action mints a borrowed session (so the banner appears immediately
   *  without waiting on /auth/me) and cleared on exit. */
  setImpersonation(impersonation) {
    set({ impersonation: impersonation ?? null })
  },

  /** Account switcher — pull the authoritative list of accounts known to
   *  this browser. Called when the profile menu opens so the list is
   *  fresh (the bootstrap copy can be stale after an add-account). */
  async refreshAccounts() {
    try {
      const res = await api.get('/auth/accounts')
      const list = Array.isArray(res.data?.data) ? res.data.data : []
      set({ accounts: list })
      return list
    } catch (err) {
      extractApiError(err)
      return get().accounts
    }
  },

  /** Account switcher — switch the active session to another account this
   *  browser has authenticated. The server re-mints + swaps the session
   *  cookie; we then HARD-reload so React Query's per-tenant caches are
   *  dropped and the correct role shell mounts fresh (mirrors the
   *  impersonation-exit reload). Navigating to '/' lets the root route +
   *  landingFor pick the right home for the switched-to account's role. */
  async switchAccount(userId) {
    // Page Performance: send this account's pending reports while the
    // cookie is still this account's (the switch swaps it).
    flushPagePerf()
    await api.post('/auth/switch', { userId })
    window.location.assign('/')
  },

  /** Account switcher — sign into an additional account on this device.
   *  Routes to the sign-in page in "add" mode (it won't bounce a
   *  signed-in user away); after signing in, the new account becomes
   *  active and joins the switcher. */
  addAccount() {
    window.location.assign('/sign-in?add=1')
  },

  /** Apply a partial user update locally without re-fetching from
   *  the server. Useful when a mutation endpoint (e.g.
   *  `PATCH /users/me`) already returns the updated fields — we
   *  can splice them into the store immediately so the UI updates
   *  even if the follow-up `/auth/me` round-trip fails (e.g.
   *  expired access cookie waiting on a refresh). The token
   *  middleware on the next request will catch up the rest. */
  applyUserPatch(patch) {
    const current = get().user
    if (!current) return
    set({ user: { ...current, ...patch } })
  },

  /** True if the current user's effective Work Role grants `key`.
   *  Delegates to the pure helper in `lib/permissions.js` so the
   *  same logic is reusable from outside Zustand selectors. */
  hasOperation(key) {
    return userHasOperation(get().user, key)
  },

  /** True if the current tenant's plan includes the capability `key`
   *  (e.g. `workflows`, `gamification`, `sops`). Mirrors the backend
   *  `requireFeature` middleware exactly:
   *    - super_admins live outside tenants → always enabled.
   *    - FAIL-OPEN: enabled unless the tenant's `features` blob carries
   *      an explicit `false`. So `true` and `undefined` (legacy tenants
   *      whose plan predates the flag) both pass; only `false` (a plan
   *      that excludes the feature) gates. Drives sidebar visibility and
   *      the route guards. */
  hasFeature(key) {
    if (get().user?.role === 'super_admin') return true
    return get().tenant?.features?.[key] !== false
  },

  /** True if the current user holds AT LEAST ONE of the listed
   *  operations. Useful for gates with several entry points
   *  (e.g. "any reports scope is enough to surface the Reports tab"). */
  hasAnyOperation(keys) {
    return userHasAnyOperation(get().user, keys)
  },

  /** True if the current user may access Sub Admin operation-gated
   *  surfaces. `company_admin` owns the tenant and always passes;
   *  `sub_admin` now shares the full Company Admin panel, so passes too.
   *  Every other role (super_admin, user) fails. */
  hasSubAdminOperation() {
    const u = get().user
    if (!u) return false
    if (u.role === 'company_admin') return true
    // Sub Admins now share the Company Admin panel: every
    // operation-gated nav item / action button surfaces for them. The
    // dangerous, company_admin-only routes (Billing, API Keys, SOP
    // Integration) are kept hidden separately via the caOnly deny-list
    // in AdminLayout, and the backend blocks them regardless.
    if (u.role === 'sub_admin') return true
    return false
  },

  async signOut() {
    try {
      await api.post('/auth/sign-out')
    } catch (err) {
      // swallow — we still clear local state
      extractApiError(err)
    }
    // [QA-04] The shared cookies are gone — other tabs must stop rendering
    // this session.
    publishIdentity(null)
    disconnectSocket()
    // Drop every cached API response with the session that was allowed to see
    // it. The QueryClient lives for the whole PAGE, not the session, and its
    // keys name the resource rather than the viewer (`['spaces', workspaceId]`
    // …). Without this, the next person to sign in on this tab got a cache HIT
    // on the previous person's data and React Query rendered it immediately —
    // in testing a Team Leader was shown a private Space belonging to the
    // admin who had just signed out, and it did not self-correct because
    // `refetchOnWindowFocus` is off. Only a manual reload cleared it, which is
    // exactly why "refresh fixes it" was the reported workaround.
    clearQueryCache()
    // Clear the "first-workspace prompt was dismissed" flag so the next
    // sign-in re-prompts if they still have no workspaces.
    try {
      sessionStorage.removeItem('taskmagix.onboardingDismissed')
    } catch {
      /* ignore */
    }
    set({
      user: null,
      tenant: null,
      impersonation: null,
      status: 'unauthenticated',
      workspaceCount: 0,
      accounts: [],
    })
    setDisplayTimeZone(undefined)
  },

  /** Account switcher — sign out of EVERY account on this device. Clears
   *  the active session, forgets all remembered accounts, then hard-
   *  redirects to sign-in for a clean slate. */
  async signOutAll() {
    try {
      await api.post('/auth/sign-out-all')
    } catch (err) {
      extractApiError(err)
    }
    publishIdentity(null)
    disconnectSocket()
    // Same reason as signOut(): the cache outlives the session, so it must
    // go with it. This path does NOT reload the page, so nothing else would
    // drop it.
    clearQueryCache()
    try {
      sessionStorage.removeItem('taskmagix.onboardingDismissed')
    } catch {
      /* ignore */
    }
    set({
      user: null,
      tenant: null,
      impersonation: null,
      status: 'unauthenticated',
      workspaceCount: 0,
      accounts: [],
    })
    setDisplayTimeZone(undefined)
    window.location.assign('/sign-in')
  },

  /**
   * Server-initiated logout. Triggered by the axios interceptor on
   * terminal error codes (account deleted, tenant suspended, password
   * changed, etc.) and by the realtime `session.terminated` socket event.
   * Idempotent — safe to call repeatedly while one teardown is in flight.
   *
   * Dedup strategy: when a tenant is deleted three signals can arrive
   * nearly simultaneously — the `session.terminated` socket event, any
   * in-flight HTTP request that returns 401/403 with `tenant_deleted`,
   * and the next `/auth/refresh` attempt's `session_expired` terminal
   * error. The `status === 'unauthenticated'` guard below catches the
   * common case, but bursty arrivals from different async callbacks
   * can race past it before zustand's `set` propagates. So we ALSO
   * give the toast a stable `id` keyed by reason — sonner collapses
   * duplicates with the same id into a single visible toast, so the
   * "Your organisation has been deleted." stack of 2-3 toasts the
   * user saw becomes one regardless of how many code paths fire.
   *
   * `opts.local` ([QA-04]) — the browser already holds no session cookies
   * (`/auth/refresh` answered `{ session: false }`). Only a SIGNED-IN tab
   * acts on it (a fresh visitor's bootstrap 401 takes the same path), and
   * the server sign-out call is skipped: there is nothing to clear, and a
   * late POST could revoke a session another tab has just started.
   */
  forceLogout(reason, message, opts = {}) {
    const state = get()
    if (state.status === 'unauthenticated') return
    if (opts.local && state.status !== 'authenticated') return
    const text =
      message || FORCE_LOGOUT_MESSAGES[reason] || 'You have been signed out.'
    // Best-effort server-side cookie clear. We don't await — local
    // teardown shouldn't wait for the network and the cookie will be
    // overwritten by the next sign-in either way.
    if (!opts.local) {
      api.post('/auth/sign-out').catch(() => {
        /* ignore */
      })
    }
    publishIdentity(null)
    disconnectSocket()
    // Same reason as signOut(): the cache outlives the session, so it must
    // go with it. This path does NOT reload the page, so nothing else would
    // drop it.
    clearQueryCache()
    try {
      sessionStorage.removeItem('taskmagix.onboardingDismissed')
    } catch {
      /* ignore */
    }
    set({
      user: null,
      tenant: null,
      impersonation: null,
      status: 'unauthenticated',
      workspaceCount: 0,
    })
    setDisplayTimeZone(undefined)
    // Toast last so users see it even if the route flips immediately.
    // `id` keyed by reason (e.g. `force-logout-tenant_deleted`) makes
    // sonner deduplicate concurrent calls into a single toast — even
    // if forceLogout itself somehow runs more than once across the
    // socket / HTTP / refresh paths during a tenant-delete cascade.
    toast.error(text, {
      id: `force-logout-${reason ?? 'unknown'}`,
      duration: 8000,
    })
  },

  setRememberedEmail(emailId) {
    const { rememberMe } = get()
    if (rememberMe) {
      try {
        localStorage.setItem(REMEMBERED_EMAIL_KEY, emailId)
      } catch {
        /* ignore */
      }
      set({ rememberedEmail: emailId })
    }
  },
}))

// Bridge between the axios interceptor (which can't import the store
// without creating a cycle) and the store's forceLogout action. The
// `taskmagix:force-logout` custom event is dispatched from lib/api.js
// whenever a request returns a terminal session-ending error code.
if (typeof window !== 'undefined') {
  window.addEventListener('taskmagix:force-logout', (event) => {
    const { reason, message, local } = event.detail ?? {}
    useAuthStore.getState().forceLogout(reason, message, { local })
  })

  // [QA-04] Another tab signed out, signed in as someone else, switched
  // company or started impersonating: the shared cookies now belong to a
  // different identity than this tab renders. Drop every cached response
  // and reload, so the tab re-bootstraps as whoever the cookies belong to
  // (or lands on sign-in) instead of mixing one session's header and
  // permission gates with another session's data.
  installIdentitySync({
    getState: () => useAuthStore.getState(),
    onStale: () => {
      clearQueryCache()
      disconnectSocket()
      // Page Performance: the cookie now belongs to another identity —
      // discard this tab's pending reports instead of sending them.
      clearPagePerf()
      window.location.reload()
    },
  })
}
